import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  Checkbox,
  FormControlLabel,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import PreviewIcon from '@mui/icons-material/Preview';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FlexService from '../../services/FlexService';

const CriteriaBuilderPanel = ({
  mode = 'create',
  zoneData,
  config,
  colors,
  onSave,
  onCancel,
}) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Step 1: Zone fields (zoneId is system-generated on create, editable on edit)
  const [zoneId, setZoneId] = useState('');
  const [zoneName, setZoneName] = useState('');
  const [zoneType, setZoneType] = useState('');
  const [description, setDescription] = useState('');
  const [isDynamic, setIsDynamic] = useState(true);
  const [errors, setErrors] = useState({});

  // Step 2: Criteria rows
  const [criteria, setCriteria] = useState([]);
  const [filterFields, setFilterFields] = useState([]);
  const [operators, setOperators] = useState([]);
  const [zoneTypes, setZoneTypes] = useState([]);

  // Preview state
  const [previewResult, setPreviewResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  // Loading options
  const [optionsLoading, setOptionsLoading] = useState(true);

  // Load all options with a single WMS call via zone_filter_fields
  // The script returns { filterFields: [...], operators: [...], zoneTypes: [...] }
  useEffect(() => {
    const loadOptions = async () => {
      setOptionsLoading(true);
      try {
        const scriptName = config.filterFieldsScript || config.zoneTypesScript || config.operatorsScript;
        if (!scriptName) { setOptionsLoading(false); return; }

        const res = await FlexService.executeScript({ scriptName, context: {} });
        if (res.success && res.context?.result) {
          const data = res.context.result;
          setFilterFields(Array.isArray(data.filterFields) ? data.filterFields : Array.isArray(data) ? data : []);
          setOperators(Array.isArray(data.operators) ? data.operators : []);
          setZoneTypes(Array.isArray(data.zoneTypes) ? data.zoneTypes : []);
        } else {
          console.error('Failed to load zone options:', res.errorMessage || res.context?.error_message);
          setSaveError('Failed to load zone options. Check WMS connection.');
        }
      } catch (err) {
        console.error('Failed to load criteria options:', err);
      } finally {
        setOptionsLoading(false);
      }
    };
    loadOptions();
  }, [config]);

  // Populate edit data
  useEffect(() => {
    if (mode === 'edit' && zoneData) {
      setZoneId(zoneData.zoneId || '');
      setZoneName(zoneData.zoneName || '');
      setZoneType(zoneData.zoneType || '');
      setDescription(zoneData.description || '');
      setIsDynamic(zoneData.isDynamic !== false);
      if (zoneData.criteria && Array.isArray(zoneData.criteria)) {
        setCriteria(zoneData.criteria.map((c, i) => ({ ...c, _key: i })));
      }
    }
  }, [mode, zoneData]);

  // Load full zone data for edit mode
  useEffect(() => {
    if (mode === 'edit' && zoneData && config.loadScript && !zoneData.criteria) {
      const loadZone = async () => {
        try {
          const res = await FlexService.executeScript({
            scriptName: config.loadScript,
            context: { _payload: { zoneId: zoneData.zoneId } },
          });
          if (res.success && res.context?.result) {
            const data = res.context.result;
            setZoneName(data.zoneName || zoneData.zoneName || '');
            setZoneType(data.zoneType || zoneData.zoneType || '');
            setDescription(data.description || '');
            setIsDynamic(data.isDynamic !== false);
            if (data.criteria && Array.isArray(data.criteria)) {
              setCriteria(data.criteria.map((c, i) => ({ ...c, _key: i })));
            }
          }
        } catch (err) {
          console.error('Failed to load zone data:', err);
        }
      };
      loadZone();
    }
  }, [mode, zoneData, config.loadScript]);

  const validateStep1 = useCallback(() => {
    const errs = {};
    if (!zoneName.trim()) errs.zoneName = 'Zone Name is required';
    if (!zoneType) errs.zoneType = 'Zone Type is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [zoneName, zoneType]);

  const handleNext = useCallback(() => {
    if (validateStep1()) {
      if (isDynamic) {
        setStep(2);
        if (criteria.length === 0) {
          setCriteria([{ _key: 0, filterField: '', filterOperator: '', filterValue: '', filterValueTo: '', logicalOperator: 'AND' }]);
        }
      } else {
        handleSave();
      }
    }
  }, [validateStep1, isDynamic, criteria.length]);

  const addCriteriaRow = useCallback(() => {
    setCriteria((prev) => [
      ...prev,
      { _key: Date.now(), filterField: '', filterOperator: '', filterValue: '', filterValueTo: '', logicalOperator: 'AND' },
    ]);
  }, []);

  const removeCriteriaRow = useCallback((key) => {
    setCriteria((prev) => prev.filter((c) => c._key !== key));
  }, []);

  const updateCriteriaRow = useCallback((key, field, value) => {
    setCriteria((prev) => prev.map((c) => (c._key === key ? { ...c, [field]: value } : c)));
    setPreviewResult(null);
  }, []);

  const handlePreview = useCallback(async () => {
    if (!config.previewScript) return;
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const cleanCriteria = criteria
        .filter((c) => c.filterField && c.filterOperator && c.filterValue)
        .map(({ _key, ...rest }) => rest);
      const res = await FlexService.executeScript({
        scriptName: config.previewScript,
        context: { _payload: { criteria: cleanCriteria } },
      });
      if (res.success) {
        setPreviewResult(res.context?.result || { matchCount: 0, locations: [] });
      } else {
        setSaveError(res.context?.error_message || res.errorMessage || 'Preview failed');
      }
    } catch (err) {
      setSaveError(err.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  }, [criteria, config.previewScript]);

  const handleSave = useCallback(async () => {
    if (step === 1 && !validateStep1()) return;

    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        zoneName: zoneName.trim(),
        zoneType,
        description: description.trim(),
        isDynamic,
      };
      // Only include zoneId when editing (it's system-generated on create)
      if (mode === 'edit' && zoneId) {
        payload.zoneId = zoneId;
      }

      if (isDynamic && criteria.length > 0) {
        payload.criteria = criteria
          .filter((c) => c.filterField && c.filterOperator && c.filterValue)
          .map(({ _key, ...rest }, idx) => ({ ...rest, criteriaSequence: idx + 1 }));
      }

      const scriptName = mode === 'edit' ? config.updateScript : config.saveScript;
      const res = await FlexService.executeScript({
        scriptName,
        context: { _payload: payload },
      });

      // res.success only means the Command ran; a zone the warehouse refused says so in context.success
      if (res.success && res.context?.success !== false) {
        if (onSave) onSave(res.context?.result);
      } else {
        setSaveError(res.context?.error_message || res.errorMessage || 'Save failed');
      }
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [step, validateStep1, zoneId, zoneName, zoneType, description, isDynamic, criteria, mode, config, onSave]);

  const getOptionLabel = (item) => {
    if (typeof item === 'string') return item;
    return item.label || item.value || item.listValueValue || item.name || String(item);
  };

  const getOptionValue = (item) => {
    if (typeof item === 'string') return item;
    return item.value || item.listValueValue || item.name || String(item);
  };

  if (optionsLoading) {
    return (
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, bgcolor: colors.background, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={32} sx={{ color: '#22c55e' }} />
      </Box>
    );
  }

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      bgcolor: colors.background,
      '& fieldset': { borderColor: colors.border },
      '&:hover fieldset': { borderColor: '#22c55e' },
      '&.Mui-focused fieldset': { borderColor: '#22c55e' },
    },
    '& .MuiInputBase-input': { color: colors.textPrimary },
    '& .MuiInputLabel-root': { color: colors.textMuted },
  };

  return (
    <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, bgcolor: colors.background, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface }}>
        <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          {mode === 'edit' ? `Edit Zone: ${zoneId}` : 'Create Zone'} {isDynamic ? `— Step ${step} of 2` : ''}
        </Typography>
        <IconButton size="small" onClick={onCancel} sx={{ color: colors.textMuted }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {/* Step 1: Zone Details */}
        {step === 1 && (
          <Box sx={{ maxWidth: 600 }}>
            <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 2 }}>
              Zone Details
            </Typography>

            {mode === 'edit' && zoneId && (
              <TextField
                fullWidth size="small" label="Zone ID" value={zoneId}
                disabled
                sx={{ mb: 2, ...inputSx }}
              />
            )}
            <TextField
              fullWidth size="small" label="Zone Name" value={zoneName}
              onChange={(e) => { setZoneName(e.target.value); setErrors((p) => ({ ...p, zoneName: null })); }}
              error={!!errors.zoneName} helperText={errors.zoneName}
              sx={{ mb: 2, ...inputSx }}
            />
            <FormControl fullWidth size="small" sx={{ mb: 2 }} error={!!errors.zoneType}>
              {/* labelId names each dropdown for screen readers (and getByRole) */}
              <InputLabel id="zone-type-label" sx={{ color: colors.textMuted }}>Zone Type</InputLabel>
              <Select
                labelId="zone-type-label"
                value={zoneType} label="Zone Type"
                onChange={(e) => { setZoneType(e.target.value); setErrors((p) => ({ ...p, zoneType: null })); }}
                sx={{ borderRadius: 0, bgcolor: colors.background, color: colors.textPrimary, '& fieldset': { borderColor: colors.border } }}
              >
                {zoneTypes.map((t) => (
                  <MenuItem key={getOptionValue(t)} value={getOptionValue(t)}>{getOptionLabel(t)}</MenuItem>
                ))}
              </Select>
              {errors.zoneType && <Typography variant="caption" sx={{ color: '#ef4444', ml: 1.5, mt: 0.5 }}>{errors.zoneType}</Typography>}
            </FormControl>
            <TextField
              fullWidth size="small" label="Description" value={description} multiline rows={2}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mb: 2, ...inputSx }}
            />
            <FormControlLabel
              control={
                <Checkbox checked={isDynamic} onChange={(e) => setIsDynamic(e.target.checked)}
                  sx={{ color: colors.textMuted, '&.Mui-checked': { color: '#22c55e' } }} />
              }
              label={<Typography variant="body2" sx={{ color: colors.textPrimary }}>Dynamic (criteria-based membership)</Typography>}
            />
          </Box>
        )}

        {/* Step 2: Criteria Rules */}
        {step === 2 && (
          <Box>
            <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 2 }}>
              Criteria Rules — Define filters to auto-resolve zone locations
            </Typography>

            {criteria.map((row, idx) => (
              <Box key={row._key} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
                {idx > 0 && (
                  <FormControl size="small" sx={{ minWidth: 70 }}>
                    <Select
                      inputProps={{ 'aria-label': 'And / Or' }}
                      value={row.logicalOperator || 'AND'}
                      onChange={(e) => updateCriteriaRow(row._key, 'logicalOperator', e.target.value)}
                      sx={{ borderRadius: 0, bgcolor: colors.background, color: colors.textPrimary, fontSize: '0.75rem', '& fieldset': { borderColor: colors.border } }}
                    >
                      <MenuItem value="AND">AND</MenuItem>
                      <MenuItem value="OR">OR</MenuItem>
                    </Select>
                  </FormControl>
                )}
                {idx === 0 && <Box sx={{ minWidth: 70 }} />}

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id={`criteria-field-label-${row._key}`} sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>Field</InputLabel>
                  <Select
                    labelId={`criteria-field-label-${row._key}`}
                    value={row.filterField || ''} label="Field"
                    onChange={(e) => updateCriteriaRow(row._key, 'filterField', e.target.value)}
                    sx={{ borderRadius: 0, bgcolor: colors.background, color: colors.textPrimary, fontSize: '0.8rem', '& fieldset': { borderColor: colors.border } }}
                  >
                    {filterFields.map((f) => (
                      <MenuItem key={getOptionValue(f)} value={getOptionValue(f)}>{getOptionLabel(f)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id={`criteria-operator-label-${row._key}`} sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>Operator</InputLabel>
                  <Select
                    labelId={`criteria-operator-label-${row._key}`}
                    value={row.filterOperator || ''} label="Operator"
                    onChange={(e) => updateCriteriaRow(row._key, 'filterOperator', e.target.value)}
                    sx={{ borderRadius: 0, bgcolor: colors.background, color: colors.textPrimary, fontSize: '0.8rem', '& fieldset': { borderColor: colors.border } }}
                  >
                    {operators.map((o) => (
                      <MenuItem key={getOptionValue(o)} value={getOptionValue(o)}>{getOptionLabel(o)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small" label="Value" value={row.filterValue || ''}
                  onChange={(e) => updateCriteriaRow(row._key, 'filterValue', e.target.value)}
                  sx={{ flex: 1, ...inputSx, '& .MuiInputBase-input': { color: colors.textPrimary, fontSize: '0.8rem' } }}
                />

                {(row.filterOperator || '').toUpperCase() === 'BETWEEN' && (
                  <TextField
                    size="small" label="Value To" value={row.filterValueTo || ''}
                    onChange={(e) => updateCriteriaRow(row._key, 'filterValueTo', e.target.value)}
                    sx={{ flex: 1, ...inputSx, '& .MuiInputBase-input': { color: colors.textPrimary, fontSize: '0.8rem' } }}
                  />
                )}

                <IconButton size="small" onClick={() => removeCriteriaRow(row._key)} sx={{ color: colors.textMuted, mt: 0.5 }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            <Button
              size="small" startIcon={<AddIcon />} onClick={addCriteriaRow}
              sx={{ color: '#22c55e', textTransform: 'none', mb: 2 }}
            >
              Add Criteria
            </Button>

            {/* Preview section */}
            <Divider sx={{ borderColor: colors.border, my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Button
                size="small" variant="outlined" startIcon={previewing ? <CircularProgress size={14} /> : <PreviewIcon />}
                onClick={handlePreview} disabled={previewing || criteria.filter((c) => c.filterField && c.filterOperator && c.filterValue).length === 0}
                sx={{ color: '#3b82f6', borderColor: '#3b82f6', textTransform: 'none', borderRadius: 0, '&:hover': { borderColor: '#2563eb', bgcolor: 'rgba(59,130,246,0.08)' } }}
              >
                Preview
              </Button>
              {previewResult && (
                <Chip
                  label={((n) => `${n} ${n === 1 ? 'location matches' : 'locations match'}`)(previewResult.matchCount ?? previewResult.locations?.length ?? 0)}
                  size="small"
                  sx={{ bgcolor: '#22c55e', color: '#fff', fontWeight: 600 }}
                />
              )}
            </Box>

            {previewResult?.locations && previewResult.locations.length > 0 && (
              <Box sx={{ maxHeight: 200, overflow: 'auto', border: `1px solid ${colors.border}`, p: 1, mb: 2 }}>
                <Typography variant="caption" sx={{ color: colors.textMuted, mb: 0.5, display: 'block' }}>
                  Matching Locations:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {previewResult.locations.map((loc) => (
                    <Chip
                      key={typeof loc === 'string' ? loc : loc.locationId}
                      label={typeof loc === 'string' ? loc : loc.locationId}
                      size="small" variant="outlined"
                      sx={{ borderColor: colors.border, color: colors.textSecondary, fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {saveError && (
          <Typography variant="body2" sx={{ color: '#ef4444', mt: 2 }}>{saveError}</Typography>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, borderTop: `1px solid ${colors.border}`, bgcolor: colors.surface }}>
        <Box>
          {step === 2 && (
            <Button
              size="small" startIcon={<ArrowBackIcon />} onClick={() => setStep(1)}
              sx={{ color: colors.textSecondary, textTransform: 'none', borderRadius: 0 }}
            >
              Back
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small" onClick={onCancel}
            sx={{ color: colors.textMuted, textTransform: 'none', borderRadius: 0 }}
          >
            Cancel
          </Button>
          {step === 1 && (
            <Button
              size="small" variant="contained"
              startIcon={isDynamic ? <ArrowForwardIcon /> : <SaveIcon />}
              onClick={handleNext} disabled={saving}
              sx={{ bgcolor: '#22c55e', color: '#fff', textTransform: 'none', borderRadius: 0, '&:hover': { bgcolor: '#16a34a' } }}
            >
              {isDynamic ? 'Next: Criteria' : (mode === 'edit' ? 'Update Zone' : 'Create Zone')}
            </Button>
          )}
          {step === 2 && (
            <Button
              size="small" variant="contained" startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
              onClick={handleSave} disabled={saving}
              sx={{ bgcolor: '#22c55e', color: '#fff', textTransform: 'none', borderRadius: 0, '&:hover': { bgcolor: '#16a34a' } }}
            >
              {mode === 'edit' ? 'Update Zone' : 'Create Zone'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CriteriaBuilderPanel;
